import { Feature as GeoFeature, Polygon as GeoPolygon } from "geojson";
import { QueryGetProductsOrderByColumn, SortOrder } from "generated/graphql";
import { FeatureCollection } from "geojson";
import * as turf from "@turf/turf";
import useGetProducts from "hooks/products/useGetProducts";
import { MapOutput } from "interfaces/interfaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GroupedAssignment,
  ProductOption,
} from "modules/proposals/draft/measurements/measurementTypes";
import {
  buildStageMeasurementDrafts,
  calculateFeatureMetrics,
  ExistingStageMeasurement,
  getMeasurementValueForJob,
  normalizeStageMapData,
  StageMeasurementDraft,
} from "modules/proposals/draft/measurements/stageMeasurements";
import { splitPixelPolygonByLine } from "modules/aiMapping/aiMappingGeometry";

export type PersistMeasurementsFn = (
  nextMapData: FeatureCollection | null,
  nextMeasurements: StageMeasurementDraft[],
  options?: { refetch?: boolean }
) => Promise<unknown | null>;

type UseMapMeasurementsArgs = {
  enabled: boolean;
  resetDeps: unknown[];
  mapDataJson: string | null | undefined;
  existingMeasurements: ExistingStageMeasurement[];
  persistMeasurements: PersistMeasurementsFn;
  canPersist: boolean;
  initialDrawerOpen?: boolean | ((measurements: StageMeasurementDraft[]) => boolean);
  preserveMeasurementValueFeatureIds?: ReadonlySet<string>;
};

export default function useMapMeasurements({
  enabled,
  resetDeps,
  mapDataJson,
  existingMeasurements,
  persistMeasurements,
  canPersist,
  initialDrawerOpen = false,
  preserveMeasurementValueFeatureIds,
}: UseMapMeasurementsArgs) {
  const [mapData, setMapData] = useState<FeatureCollection | null>(null);
  const [measurements, setMeasurements] = useState<StageMeasurementDraft[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const preserveValueFeatureIdsRef = useRef(preserveMeasurementValueFeatureIds);
  preserveValueFeatureIdsRef.current = preserveMeasurementValueFeatureIds;
  const {
    data: products,
    loading: productsLoading,
    getAllData,
    paginatorInfo,
  } = useGetProducts({
    first: 200,
    page: 1,
    orderBy: [{ column: QueryGetProductsOrderByColumn.NAME, order: SortOrder.ASC }],
  });

  useEffect(() => {
    if (!productsLoading && paginatorInfo?.total && (products?.length ?? 0) < paginatorInfo.total) {
      getAllData();
    }
  }, [getAllData, paginatorInfo?.total, products?.length, productsLoading]);

  useEffect(() => {
    if (!enabled) return;

    const parsedMapData = mapDataJson ? JSON.parse(mapDataJson) : null;
    const normalizedMapData = normalizeStageMapData(parsedMapData);
    const nextMeasurements = buildStageMeasurementDrafts(
      normalizedMapData,
      existingMeasurements,
      preserveValueFeatureIdsRef.current ?? null
    );

    setMapData(normalizedMapData);
    setMeasurements(nextMeasurements);
    setSelectedFeatureId(nextMeasurements[0]?.featureId ?? null);
    setIsDrawerOpen(
      typeof initialDrawerOpen === "function"
        ? initialDrawerOpen(nextMeasurements)
        : initialDrawerOpen
    );
    setIsDirty(false);
  }, [enabled, ...resetDeps]);

  const assignedMeasurements = useMemo(
    () => measurements.filter((m) => m.productId),
    [measurements]
  );

  const groupedAssignments = useMemo<GroupedAssignment[]>(
    () =>
      Object.values(
        assignedMeasurements.reduce<Record<string, GroupedAssignment>>((acc, m) => {
          const id = m.productId as string;
          const current = acc[id] ?? {
            productId: id,
            productName: m.productName || "Product",
            total: 0,
            hasScreenshot: false,
          };
          current.total += m.measurementValue ?? 0;
          current.hasScreenshot = current.hasScreenshot || m.isScreenshotTarget;
          acc[id] = current;
          return acc;
        }, {})
      ),
    [assignedMeasurements]
  );

  const persist = useCallback(
    async (
      nextMapData: FeatureCollection | null,
      nextMeasurements: StageMeasurementDraft[],
      options?: { refetch?: boolean }
    ) => {
      const normalizedMapData = normalizeStageMapData(nextMapData);
      if (!normalizedMapData) return null;

      setPersisting(true);
      try {
        const result = await persistMeasurements(nextMapData, nextMeasurements, options);
        setMapData(normalizedMapData);
        setMeasurements(nextMeasurements);
        setIsDirty(false);
        return result;
      } finally {
        setPersisting(false);
      }
    },
    [persistMeasurements]
  );

  const rebuildMeasurements = useCallback(
    (nextMapData: FeatureCollection | null, sourceMeasurements: StageMeasurementDraft[]) => {
      const normalizedMapData = normalizeStageMapData(nextMapData);
      const nextMeasurements = buildStageMeasurementDrafts(
        normalizedMapData,
        sourceMeasurements.map((m) => ({
          geojsonFeatureId: m.featureId,
          measurementJob: m.measurementJob,
          measurementValue: m.measurementValue,
          label: m.label,
          sortOrder: m.sortOrder,
          productId: m.productId,
          isScreenshotTarget: m.isScreenshotTarget,
          product: m.productId
            ? {
                id: m.productId,
                name: m.productName ?? "",
                measurementJob: m.measurementJob,
                dailyUnitsComplete: m.defaultDailyUnitsComplete,
              }
            : null,
        })),
        preserveValueFeatureIdsRef.current ?? null
      );
      return { normalizedMapData, nextMeasurements };
    },
    []
  );

  const updateMeasurementProduct = useCallback(
    (featureId: string, product: ProductOption | null) => {
      setMeasurements((current) =>
        current.map((m) => {
          if (m.featureId !== featureId) return m;
          const nextMeasurementJob = product?.measurementJob ?? null;
          return {
            ...m,
            productId: product?.id ?? null,
            productName: product?.name ?? null,
            measurementJob: nextMeasurementJob,
            measurementValue: getMeasurementValueForJob(m.metrics, nextMeasurementJob),
            isScreenshotTarget: product ? m.isScreenshotTarget : false,
            defaultDailyUnitsComplete: product?.dailyUnitsComplete ?? null,
          };
        })
      );
      setIsDirty(true);
    },
    []
  );

  const toggleScreenshot = useCallback((featureId: string, checked: boolean) => {
    setMeasurements((current) =>
      current.map((m) => (m.featureId === featureId ? { ...m, isScreenshotTarget: checked } : m))
    );
    setIsDirty(true);
  }, []);

  const applyMeasurementEdits = useCallback(
    (
      editor: (
        features: GeoFeature[],
        drafts: StageMeasurementDraft[]
      ) => { features: GeoFeature[]; drafts: StageMeasurementDraft[] }
    ) => {
      if (!mapData) return;
      const features = (mapData.features ?? []) as GeoFeature[];
      const outcome = editor(features, measurements);
      const normalized = normalizeStageMapData({
        ...mapData,
        features: outcome.features,
      } as FeatureCollection);
      setMapData(normalized);
      setMeasurements(outcome.drafts);
      setIsDirty(true);
    },
    [mapData]
  );

  const removeMeasurement = useCallback(
    (featureId: string) => {
      applyMeasurementEdits((features, drafts) => ({
        features: features.filter(
          (feature) => String(feature.properties?.featureId) !== String(featureId)
        ),
        drafts: drafts.filter((draft) => String(draft.featureId) !== String(featureId)),
      }));
    },
    [applyMeasurementEdits]
  );

  const mergeMeasurements = useCallback(
    (featureIds: string[]) => {
      const byFeatureId = new Map(measurements.map((draft) => [String(draft.featureId), draft]));
      const targets = featureIds
        .map((featureId) => byFeatureId.get(String(featureId)))
        .filter((draft): draft is StageMeasurementDraft => !!draft);
      if (targets.length < 2) return;

      const polygons = targets.map((draft) => {
        const feature = JSON.parse(draft.featureGeojson) as GeoFeature<
          GeoPolygon,
          { featureId?: string }
        >;
        return turf.polygon([feature.geometry.coordinates[0]] as any);
      });
      let union = polygons[0];
      for (let index = 1; index < polygons.length; index += 1) {
        const next = turf.union(union, polygons[index]);
        if (!next || next.geometry.type !== "Polygon") return;
        union = next as any;
      }

      const [primary] = targets;
      const primaryFeature = JSON.parse(primary.featureGeojson) as GeoFeature;
      const ring = (union.geometry as any).coordinates[0];
      const mergedFeature: GeoFeature = {
        ...primaryFeature,
        geometry: { type: "Polygon", coordinates: [ring] },
      };
      const metrics = calculateFeatureMetrics(mergedFeature as any);
      applyMeasurementEdits((features, drafts) => ({
        features: features
          .map((feature) =>
            String(feature.properties?.featureId) === String(primary.featureId)
              ? mergedFeature
              : feature
          )
          .filter(
            (feature) =>
              featureIds.includes(String(feature.properties?.featureId)) === false ||
              String(feature.properties?.featureId) === String(primary.featureId)
          ),
        drafts: drafts
          .filter(
            (draft) =>
              !featureIds.includes(String(draft.featureId)) ||
              String(draft.featureId) === String(primary.featureId)
          )
          .map((draft) =>
            String(draft.featureId) === String(primary.featureId)
              ? {
                  ...draft,
                  featureGeojson: JSON.stringify(mergedFeature),
                  metrics,
                  measurementValue: getMeasurementValueForJob(metrics, draft.measurementJob),
                }
              : draft
          ),
      }));
    },
    [applyMeasurementEdits]
  );

  const splitMeasurement = useCallback(
    (featureId: string, line: [[number, number], [number, number]]) => {
      const draft = measurements.find((item) => String(item.featureId) === String(featureId));
      if (!draft || draft.measurementType !== "AREA") return;
      const feature = JSON.parse(draft.featureGeojson) as GeoFeature<
        GeoPolygon,
        { featureId?: string }
      >;
      if (feature.geometry.type !== "Polygon") return;
      const flat = feature.geometry.coordinates[0].flatMap(([lng, lat]) => [lng, lat]);
      const pieces = splitPixelPolygonByLine(flat, line[0], line[1]);
      if (!pieces) return;

      const toFeature = (flatRing: number[], featureId: string): GeoFeature => {
        const ring: [number, number][] = [];
        for (let index = 0; index + 1 < flatRing.length; index += 2) {
          ring.push([flatRing[index], flatRing[index + 1]]);
        }
        return {
          ...feature,
          properties: { ...feature.properties, featureId },
          geometry: { type: "Polygon", coordinates: [ring] },
        };
      };
      const nextId = `${featureId}-split-${Math.round(Math.random() * 100000)}`;
      const firstFeature = toFeature(pieces[0], String(featureId));
      const secondFeature = toFeature(pieces[1], nextId);
      const firstMetrics = calculateFeatureMetrics(firstFeature as any);
      const secondMetrics = calculateFeatureMetrics(secondFeature as any);

      applyMeasurementEdits((features, drafts) => ({
        features: features.flatMap((current) =>
          String(current.properties?.featureId) === String(featureId)
            ? [firstFeature, secondFeature]
            : [current]
        ),
        drafts: drafts.flatMap((current) =>
          String(current.featureId) === String(featureId)
            ? [
                {
                  ...current,
                  featureGeojson: JSON.stringify(firstFeature),
                  metrics: firstMetrics,
                  measurementValue: getMeasurementValueForJob(firstMetrics, current.measurementJob),
                },
                {
                  ...current,
                  featureId: secondFeature.properties.featureId as string,
                  featureGeojson: JSON.stringify(secondFeature),
                  metrics: secondMetrics,
                  measurementValue: getMeasurementValueForJob(
                    secondMetrics,
                    current.measurementJob
                  ),
                },
              ]
            : [current]
        ),
      }));
    },
    [applyMeasurementEdits, measurements]
  );

  const save = useCallback(() => persist(mapData, measurements), [mapData, measurements, persist]);

  // Keep a ref so handleMapChange always reads the latest measurements without
  // needing a new function identity. Map.tsx registers event handlers once on mount
  // and never re-registers them, so a stable callback reference is critical.
  const measurementsRef = useRef(measurements);
  measurementsRef.current = measurements;

  const handleMapChange = useCallback(
    (data: MapOutput) => {
      const current = measurementsRef.current;
      const { normalizedMapData, nextMeasurements } = rebuildMeasurements(data.geojson, current);
      // Map edit events may be emitted faster than React can render. Update this
      // synchronously so a following event preserves the assignments just rebuilt.
      measurementsRef.current = nextMeasurements;
      setMapData(normalizedMapData);
      setMeasurements(nextMeasurements);
      setSelectedFeatureId((prev) => prev ?? nextMeasurements[0]?.featureId ?? null);
      if (nextMeasurements.length > current.length) {
        setSelectedFeatureId(nextMeasurements[nextMeasurements.length - 1]?.featureId ?? null);
      }
      setIsDirty(true);
    },
    [rebuildMeasurements]
  );

  const handleFeatureSelect = useCallback((featureId: string) => {
    setSelectedFeatureId(featureId);
  }, []);

  const toggleDrawer = useCallback(() => setIsDrawerOpen((current) => !current), []);

  return {
    mapData,
    measurements,
    selectedFeatureId,
    isDrawerOpen,
    persisting,
    isDirty,
    products,
    productsLoading,
    canSave: !!mapData && canPersist,
    groupedAssignments,
    assignedMeasurements,
    selectFeature: setSelectedFeatureId,
    updateMeasurementProduct,
    toggleScreenshot,
    removeMeasurement,
    mergeMeasurements,
    splitMeasurement,
    save,
    persist,
    handleMapChange,
    handleFeatureSelect,
    toggleDrawer,
  };
}
